import type { AnySceneObjectT } from "tweenly"
import { postFile } from "../ajax.helpers"
import { S3_PREFIX } from "../file.helpers"

/**
 * Result of finding and uploading images
 */
export type MissingImage = {
    objectId: string
    objectTitle: string
    imageRef: string
}

export type UploadedAsset = {
    name: string
    slug: string
    url: string
    type: "image"
    size: number
}

export type UploadImagesResult = {
    objects: AnySceneObjectT[]
    uploadedAssets: UploadedAsset[]
    missingImages: MissingImage[]
}

/**
 * Find a blob in the imageBlobs map by trying multiple key variations:
 * exact match, with "images/" prefix, or just by filename.
 *
 * Uses Map.forEach() instead of Array.from() to avoid duplicating
 * all Map entries in memory - important for large image sets.
 */
export const findImageBlob = (ref: string, imageBlobs: Map<string, Blob>): Blob | undefined => {
    // 1. Exact match (e.g. "images/photo.png" in both ref and map)
    if (imageBlobs.has(ref)) return imageBlobs.get(ref)

    // 2. ref is bare filename like "photo.png" but map has "images/photo.png"
    let result: Blob | undefined
    imageBlobs.forEach((blob, key) => {
        if (!result && key.endsWith(`/${ref}`)) {
            result = blob
        }
    })
    if (result) return result

    // 3. ref has a path like "images/photo.png" but map has just "photo.png"
    const refFilename = ref.split("/").pop()
    if (refFilename && refFilename !== ref) {
        imageBlobs.forEach((blob, key) => {
            if (!result && key.split("/").pop() === refFilename) {
                result = blob
            }
        })
    }

    return result
}

/**
 * Upload images to S3 per-object and replace relative URLs with S3 public URLs.
 *
 * This is a pure function that returns uploadedAssets for the caller to register.
 * It does NOT dispatch any Redux actions - that responsibility stays in the thunk.
 *
 * The S3 key MUST contain the Tweenly object ID so that ZIP export can find and
 * package the file (it queries by object ID). Each object that references an image
 * gets its own upload, matching Tweenly's native image behavior.
 */
export const uploadAndReplaceImages = async (
    objects: AnySceneObjectT[],
    imageBlobs: Map<string, Blob>,
    graphicId: string
): Promise<UploadImagesResult> => {
    // Cache: relativePath → publicUrl (to avoid re-uploading for objects sharing the same image)
    const uploadCache = new Map<string, string>()
    let uploadCount = 0
    const missingImages: MissingImage[] = []
    const uploadedAssets: UploadedAsset[] = []

    const updatedObjects: AnySceneObjectT[] = []
    for (const obj of objects) {
        // Find backgroundImage fill (relative path OR existing S3/external URL)
        const bgFillIndex = obj.fill.findIndex(
            (s) => s.property === "backgroundImage" && typeof s.value === "string"
        )

        if (bgFillIndex === -1) {
            updatedObjects.push(obj)
            continue
        }

        const bgFill = obj.fill[bgFillIndex]
        const urlMatch = (bgFill.value as string).match(/^url\(["']?([\s\S]*?)["']?\)$/)
        if (!urlMatch) {
            updatedObjects.push(obj)
            continue
        }

        const imageUrl = urlMatch[1]
        // Extract a filename for blob lookup — works for relative paths and full URLs
        const filename = imageUrl.split("/").pop() || "image.png"

        // Try to find a matching blob in the ZIP
        const blob = findImageBlob(imageUrl, imageBlobs) || findImageBlob(filename, imageBlobs)
        if (!blob) {
            // No matching blob — track as missing (unless it's an external URL)
            if (!imageUrl.startsWith("http")) {
                missingImages.push({
                    objectId: obj.id,
                    objectTitle: obj.title || obj.id,
                    imageRef: filename,
                })
            }
            updatedObjects.push(obj)
            continue
        }

        // Upload to S3 using this object's ID as key (required for ZIP export)
        const file = new File([blob], filename, { type: blob.type || "image/png" })
        try {
            const fileMetadata = await postFile(file, {
                storage: "s3",
                key: `${S3_PREFIX}/${graphicId}/images/${obj.id}`,
                isPublic: true,
                hasFilename: true,
            })

            if (fileMetadata.publicUrl) {
                if (!uploadCache.has(imageUrl)) {
                    uploadCache.set(imageUrl, fileMetadata.publicUrl)
                    uploadCount++
                }

                // Collect asset info for caller to register
                uploadedAssets.push({
                    name: filename,
                    slug: obj.id,
                    url: fileMetadata.publicUrl,
                    type: "image",
                    size: fileMetadata.length,
                })

                const updatedFill = [...obj.fill]
                updatedFill[bgFillIndex] = {
                    ...bgFill,
                    value: `url("${fileMetadata.publicUrl}")`,
                }
                updatedObjects.push({ ...obj, fill: updatedFill })
                continue
            }
        } catch (err) {
            console.error(`Failed to upload image ${filename} for object ${obj.id}:`, err)
        }

        updatedObjects.push(obj)
    }

    if (uploadCount > 0) {
        console.log(`Uploaded ${uploadCount} unique images from ZIP`)
    }

    return { objects: updatedObjects, uploadedAssets, missingImages }
}

/**
 * Detect objects that reference local images but have no blob provided.
 * Used when no imageBlobs are available to check for missing references.
 */
export const detectMissingImageReferences = (objects: AnySceneObjectT[]): MissingImage[] => {
    const missingImages: MissingImage[] = []

    for (const obj of objects) {
        const bgFill = obj.fill.find(
            (s) => s.property === "backgroundImage" && typeof s.value === "string"
        )
        if (!bgFill) continue

        const urlMatch = (bgFill.value as string).match(/^url\(["']?([\s\S]*?)["']?\)$/)
        if (urlMatch && !urlMatch[1].startsWith("http")) {
            const filename = urlMatch[1].split("/").pop() || urlMatch[1]
            missingImages.push({
                objectId: obj.id,
                objectTitle: obj.title || obj.id,
                imageRef: filename,
            })
        }
    }

    return missingImages
}
